import React, { Component } from "react";
import PropTypes from "prop-types";

import { isDefined, isNotDefined, noop } from "../utils";
import { terminate, saveNodeType, isHoverForInteractiveType } from "./utils";
import EachTimeCycle from "./wrapper/EachTimeCycle";
import MouseLocationIndicator from "./components/MouseLocationIndicator";
import HoverTextNearMouse from "./components/HoverTextNearMouse";

class TimeCycle extends Component {
  constructor(props) {
    super(props);

    this.handleStart = this.handleStart.bind(this);
    this.handleEnd = this.handleEnd.bind(this);
    this.handleDrawCycle = this.handleDrawCycle.bind(this);

    this.handleDrag = this.handleDrag.bind(this);
    this.handleDragComplete = this.handleDragComplete.bind(this);

    this.terminate = terminate.bind(this);
    this.getSelectionState = isHoverForInteractiveType("timeCycles").bind(this);

    this.saveNodeType = saveNodeType.bind(this);
    this.nodes = [];

    this.state = {
      current: null,
      override: null,
    };
  }

  handleDrawCycle(xyValue) {
    const { current } = this.state;
    if (isDefined(current) && isDefined(current.x1)) {
      this.mouseMoved = true;
      this.setState({
        current: {
          ...current,
          x2: xyValue[0],
          y2: xyValue[1],
        },
      });
    }
  }

  handleDrag(index, xy) {
    this.setState({
      override: {
        index,
        ...xy,
      },
    });
  }

  handleDragComplete(moreProps) {
    const { timeCycles } = this.props;
    const { override } = this.state;
    if (isDefined(override)) {
      const { index, ...rest } = override;

      const newTimeCycles = timeCycles.map((each, idx) =>
        idx === index ? { ...each, ...rest, selected: true } : each,
      );
      this.setState(
        {
          override: null,
        },
        () => {
          this.props.onComplete(newTimeCycles, moreProps);
        },
      );
    }
  }

  handleStart(xyValue, moreProps) {
    const { current } = this.state;
    if (isNotDefined(current) || isNotDefined(current.x1)) {
      this.mouseMoved = false;
      this.setState(
        {
          current: {
            x1: xyValue[0],
            y1: xyValue[1],
            x2: null,
            y2: null,
          },
        },
        () => {
          this.props.onStart(moreProps);
        },
      );
    }
  }

  handleEnd(xyValue, moreProps, e) {
    const { timeCycles, appearance } = this.props;
    const { current } = this.state;

    if (this.mouseMoved && isDefined(current) && isDefined(current.x1)) {
      const currentAppearance = appearance;

      const newTimeCycles = timeCycles.concat({
        ...current,
        x2: xyValue[0],
        y2: xyValue[1],
        selected: true,
        appearance: currentAppearance,
      });

      this.setState(
        {
          current: null,
        },
        () => {
          this.props.onComplete(newTimeCycles, moreProps, e);
        },
      );
    }
  }

  render() {
    const { enabled, timeCycles, appearance, hoverText } = this.props;
    const { current, override } = this.state;

    const hoverTextWithDefaults = {
      ...TimeCycle.defaultProps.hoverText,
      ...hoverText,
    };

    const tempCycle =
      isDefined(current) && isDefined(current.x1) ? (
        <EachTimeCycle
          interactive={false}
          {...current}
          appearance={appearance}
          hoverText={hoverTextWithDefaults}
        />
      ) : null;

    return (
      <g>
        {timeCycles.map((each, idx) => {
          // Deep merge appearance
          const eachAppearance = isDefined(each.appearance)
            ? {
                ...appearance,
                ...each.appearance,
                stroke:
                  typeof each.appearance.stroke === "string"
                    ? { ...appearance.stroke, color: each.appearance.stroke }
                    : typeof each.appearance.stroke === "object"
                      ? { ...appearance.stroke, ...each.appearance.stroke }
                      : appearance.stroke,
                fill:
                  typeof each.appearance.fill === "string"
                    ? { ...appearance.fill, color: each.appearance.fill }
                    : typeof each.appearance.fill === "object"
                      ? { ...appearance.fill, ...each.appearance.fill }
                      : appearance.fill,
                font:
                  typeof each.appearance.font === "string"
                    ? { ...appearance.font, color: each.appearance.font }
                    : typeof each.appearance.font === "object"
                      ? { ...appearance.font, ...each.appearance.font }
                      : appearance.font,
              }
            : appearance;

          const props = {
            key: idx,
            ref: this.saveNodeType(idx),
            index: idx,
            ...each,
            appearance: eachAppearance,
            hoverText: hoverTextWithDefaults,

            selected: each.selected,
            hovering: each.hovering,

            onDrag: this.handleDrag,
            onDragComplete: this.handleDragComplete,

            ...(isDefined(override) && override.index === idx ? override : {}),
          };

          return <EachTimeCycle {...props} />;
        })}
        {tempCycle}
        <MouseLocationIndicator
          enabled={enabled}
          snap={false}
          shouldDisableSnap={() => true}
          onMouseDown={this.handleStart}
          onMouseMove={this.handleDrawCycle}
          onClick={this.handleEnd}
          r={4}
          stroke={appearance.stroke?.color || appearance.stroke || "#2196F3"}
          strokeWidth={2}
          opacity={1}
          data-evo-id="interactive-time-cycle"
        />
      </g>
    );
  }
}

TimeCycle.propTypes = {
  enabled: PropTypes.bool.isRequired,
  timeCycles: PropTypes.array.isRequired,
  appearance: PropTypes.object.isRequired,

  onStart: PropTypes.func,
  onComplete: PropTypes.func.isRequired,
  onSelect: PropTypes.func,

  hoverText: PropTypes.object.isRequired,
};

TimeCycle.defaultProps = {
  timeCycles: [],
  onStart: noop,
  onComplete: noop,
  onSelect: noop,

  hoverText: {
    ...HoverTextNearMouse.defaultProps,
    enable: false,
    bgHeight: "auto",
    bgWidth: "auto",
    text: "Clique e arraste para definir ciclo",
    selectedText: "",
  },
};

export default TimeCycle;
